import clsx from 'clsx'
import { memo, useEffect, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'

const NapsterLogo = '/images/logo_napster_white.svg'

import {
  useGetKnowledgeBase,
  useTwinPersonaById,
  useUploadMedia,
} from '../../hooks/apiHooks/twinHooks'
import { useSession } from '../../hooks/storeHooks/useSessionState'
import { useTwinData } from '../../hooks/storeHooks/useTwin'
import { useUser } from '../../hooks/storeHooks/useUserState'
import { useTabs } from '../../hooks/useTabs'
import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight'
import { IUserData } from '../../store/reducers/userReducer'
import { ConsentTab } from './components/ConsentTab/ConsentTab'
import { FaqTab } from './components/FaqTab'
import { InterviewTab } from './components/InterviewTab'
import { LinkedInTab } from './components/LinkedInTab'
import { MainTab } from './components/MainTab'
import { Privacy } from './components/Privacy'
import { ProfileTab } from './components/ProfileTab'
import { PublishTab } from './components/PublishTab'
import { SelfieTab } from './components/SelfieTab'
import { TabNav } from './components/TabNav'

const tabRoutes = [
  { label: 'LinkedIn Profile', path: '/linkedin' },
  { label: 'Grab a Selfie', path: '/selfie' },
  { label: 'AI Interview', path: '/interview' },
  { label: 'Knowledge & FAQs', path: '/knowledge/files' },
  { label: 'Define Profile', path: '/info' },
  { label: 'Verify Your Twin', path: '/consent' },
  { label: 'Ready', path: '/publish' },
]

const TwinGenerationPage: React.FC = memo(() => {
  const navigate = useNavigate()
  const location = useLocation()

  const {
    jobTitle,
    twinId,
    profileJson,
    setAudioUrl,
    resetTwinState,
    setKnowledge,
    isCameraReady,
  } = useTwinData()

  const { user, isLoading: isAuthLoading } = useAuth()
  const { user: storedUser } = useUser()
  const { sessionId } = useSession()
  useVisualViewportHeight()

  const { mentorId } = storedUser || { mentorId: null }
  const { data, isLoading } = useTwinPersonaById(
    mentorId || '',
    mentorId !== null,
    false,
  )

  const mentorStatus = useMemo<string | null>(
    () => data?.data?.profile?.profile?.status as string,
    [data],
  )

  const twinImageUrl = useMemo(() => {
    return data?.data?.profile?.profile?.profilePictureUrl
  }, [data])

  const [interviewRestartKey, setInterviewRestartKey] = useState(0)
  const { activeIndex, handleChange } = useTabs(tabRoutes, '/generate-twin')

  const { mutateAsync: uploadMedia } = useUploadMedia()
  const {
    mutateAsync: fetchKnowledgeBase,
    isPending: isFetchingKnowledgeBase,
  } = useGetKnowledgeBase()

  const openNextTab = () => {
    window.scrollTo(0, 0)
    handleChange(activeIndex + 1)
  }

  // this need to be in parent component because we do it in background
  const handleAudioProcessing = async (audioBlob: Blob) => {
    console.log('Starting background audio processing...')

    openNextTab()

    uploadMedia({
      file: audioBlob,
      type: 'audio',
      name: `${Date.now()}-voice-recording.wav`,
    })
      .then(audioUrl => {
        console.log('Audio uploaded successfully:', audioUrl)
        if (audioUrl) {
          setAudioUrl(audioUrl)
        }
      })
      .catch(error => {
        console.error('Error uploading audio:', error)
      })

    if (sessionId) {
      console.log('Fetching knowledge base...')
      const { knowledge, job_title } = await fetchKnowledgeBase({
        avatarSessionId: sessionId,
        linkedinProfile: profileJson || '',
      })

      setKnowledge({
        knowledge: knowledge,
        jobTitle: job_title,
      })
    }
  }

  const handleInterviewRestart = () => {
    setInterviewRestartKey(prev => prev + 1)
  }

  useEffect(() => {
    if (mentorStatus && mentorStatus !== 'Completed') {
      navigate(`/generate-twin/publish${location.search}`, { replace: true })
      return
    } else {
      navigate(`/generate-twin${location.search}`, { replace: true })
    }

    return () => {
      resetTwinState()
    }
  }, [mentorStatus, isLoading])

  const showNav = useMemo(() => {
    return (
      activeIndex !== -1 &&
      (!location.pathname.includes('/knowledge') ||
        mentorStatus !== 'Completed')
    )
  }, [activeIndex, mentorStatus, location.pathname, mentorStatus])

  return (
    <div
      className={clsx(
        'min-h-screen overflow-hidden px-4 py-8',
        'flex flex-col justify-start items-center',
        'font-dmsans text-white',
        "bg-[url('/images/twin-bg.jpeg')] bg-cover bg-center",
        'md:px-24',
        'min-1100:w-screen min-1100:justify-center',
        'sm:!min-h-[100vh]',
        {
          'sm:relative fixed': isCameraReady,
        },
      )}
    >
      {showNav && <TabNav tabRoutes={tabRoutes} activeIndex={activeIndex} />}
      <img
        src={NapsterLogo}
        alt="Napster Logo"
        className={clsx(
          '!h-[22px] mt-auto sm:mt-0 mb-4',
          'md:!h-[30px]',
          'min-1100:fixed min-1100:top-8 min-1100:left-8',
        )}
      />
      {activeIndex === -1 && (
        <MainTab
          user={
            {
              ...storedUser,
              mentorSlug: data?.data?.profile?.profile?.slug || '',
            } as IUserData
          }
          locationSearch={location.search}
          isLoading={isLoading || isAuthLoading || !storedUser}
          isReadyStatus={
            mentorStatus
              ? mentorStatus === 'ReadyToUse' || mentorStatus === 'Completed'
              : false
          }
          twinImageUrl={twinImageUrl || ''}
          openNextTab={openNextTab}
        />
      )}
      <Routes>
        <Route
          path="/linkedin"
          element={<LinkedInTab openNextTab={openNextTab} />}
        />
        <Route
          path="/selfie"
          element={<SelfieTab openNextTab={openNextTab} />}
        />
        <Route
          path="/interview"
          element={
            <InterviewTab
              key={interviewRestartKey}
              setAudioBlob={handleAudioProcessing}
              onRestart={handleInterviewRestart}
            />
          }
        />
        <Route
          path="/knowledge/*"
          element={
            <FaqTab
              openNextTab={openNextTab}
              mentorId={mentorId || ''}
              isReadyStatus={
                mentorStatus
                  ? mentorStatus === 'ReadyToUse' ||
                    mentorStatus === 'Completed'
                  : false
              }
            />
          }
        />
        {/* <Route path="/faq" element={<FaqTabPage openNextTab={openNextTab} />} /> */}

        <Route
          path="/info"
          element={
            <ProfileTab
              userData={{
                firstName: (storedUser?.firstName as string) || '',
                lastName: (storedUser?.lastName as string) || '',
                jobTitle: jobTitle || (user?.profile.jobTitle as string) || '',
                title: (user?.profile.title as string) || '',
              }}
              isFetchingKnowledge={isFetchingKnowledgeBase}
              openNextTab={openNextTab}
            />
          }
        />
        <Route
          path="/consent"
          element={<ConsentTab openNextTab={openNextTab} />}
        />
        <Route
          path="/publish"
          element={
            <PublishTab
              twinId={twinId || data?.data?.profile?.profile?.id || ''}
              locationSearch={location.search}
            />
          }
        />
      </Routes>

      {activeIndex !== -1 && <Privacy />}
    </div>
  )
})

export { TwinGenerationPage }
